import {Component, effect, inject, OnDestroy} from '@angular/core';
import {Router, RouterLink} from '@angular/router';
import {AuthService} from 'auth';

/**
 * Shown while an inbound SSO handoff from Kentico completes. For a brand-new customer RevisionHub may
 * still be receiving their account and licence, so this page waits rather than landing them logged out.
 */
@Component({
  selector: 'app-sso-waiting-component',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './sso-waiting-component.html',
  styleUrl: './sso-waiting-component.sass',
})
export class SsoWaitingComponent implements OnDestroy {
  private authService = inject(AuthService);
  private router = inject(Router);

  readonly state = this.authService.ssoState;
  readonly kenticoRetryUrl = this.authService.ssoKenticoRetryUrl;

  constructor() {
    // Nothing to wait for: on arrival (e.g. the page was bookmarked), or later, when the handoff is
    // cancelled while this page is showing (e.g. a logout in another tab). Checking only on arrival
    // left that second case on a spinner forever (review of TFS.69299).
    effect(() => {
      if (this.state() === 'idle') {
        this.router.navigate(['/']);
      }
    });
  }

  ngOnDestroy(): void {
    // Leaving the page (e.g. via the log-in link) abandons the handoff, so it cannot sign the user
    // in later behind their back. A successful handoff reloads the page, so never reaches this.
    this.authService.cancelSsoHandoff();
  }

  canRetry(): boolean {
    return this.authService.canRetrySsoHandoff();
  }

  retry(): void {
    this.authService.retrySsoHandoff();
  }
}
